import { Vector3 } from '@babylonjs/core/Maths';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { getByDynamicId } from '@models/classes/kb3d-manager';
import { Kb3dObject } from '@models/classes/kb3d-object';
import { MeshNode } from '@models/classes/meshes/mesh-node';
import { KbViewer } from '@view/kb-viewer';
import { take } from 'rxjs/operators';
import { BoundingBoxCache } from './bounding-box-cache';

export interface IMeshInfo {
    faceCount: number;
    faceNeighbors: number[][];
    facePositions: number[];
    faceNormals: number[];
}

const meshFaceInfo = new Map<string, IMeshInfo>();

export class MeshFaceEngine {
    private bbCache: BoundingBoxCache;

    constructor(private kbViewer: KbViewer) {
        this.bbCache = kbViewer.bbCache;
    }

    initialize(node: MeshNode | Mesh) {
        let meshNode: MeshNode;
        let mesh: Mesh;
        if (node instanceof Kb3dObject) {
            mesh = this.kbViewer.getRendererNode(node) as Mesh;
            meshNode = node;
        } else {
            mesh = node;
            meshNode = getByDynamicId(node.id) as MeshNode;
        }
        if (mesh instanceof Mesh && mesh.geometry) {
            this.bbCache
                .watchForChanges(meshNode)
                .pipe(take(1))
                .subscribe(() => {
                    meshFaceInfo.delete(meshNode._dynamicId);
                });

            return this.updateNodeGeometry(mesh);
        }
    }

    getMeshFaces(node: MeshNode | Mesh) {
        let result = meshFaceInfo.get(node instanceof Kb3dObject ? node._dynamicId : node.id);
        if (!result) {
            result = this.initialize(node);
        }
        return result;
    }

    /**
     * @param selection array of selected faces to expand from
     * @param limitAngle angle to limit selection to. If undefined, function will expand by 1 polygon
     */
    expandSelection(selection: number[], node: MeshNode | Mesh, limitAngle?: number) {
        const faceInfo = this.getMeshFaces(node);
        const faceNormals = faceInfo!.faceNormals;

        const selectedFaceIds: boolean[] = [];
        const newSelection: number[] = [];
        if (limitAngle !== undefined) {
            limitAngle = (limitAngle * Math.PI) / 180;
        }
        const v1 = new Vector3(),
            v2 = new Vector3();

        if (!faceInfo) {
            return selection;
        }

        for (let i = 0; i < selection.length; i++) {
            selectedFaceIds[selection[i]] = true;
        }
        for (let i = 0; i < selection.length; i++) {
            const faceId = selection[i];
            v1.copyFromFloats(faceNormals[faceId * 3], faceNormals[faceId * 3 + 1], faceNormals[faceId * 3 + 2]);
            recursiveExpandSelection(faceId, 1, v1);
        }
        for (const faceId in selectedFaceIds) {
            newSelection.push(parseInt(faceId, 10));
        }

        return newSelection;

        function recursiveExpandSelection(faceId: number, iterCount: number, normal: Vector3) {
            const neighbors = faceInfo!.faceNeighbors[faceId];
            if (limitAngle === undefined && iterCount <= 0) {
                return;
            }

            for (let i = 0; i < neighbors.length; i++) {
                const adjacentFaceId = neighbors[i];
                if (!selectedFaceIds[adjacentFaceId]) {
                    if (limitAngle !== undefined) {
                        v2.copyFromFloats(
                            faceNormals[adjacentFaceId * 3],
                            faceNormals[adjacentFaceId * 3 + 1],
                            faceNormals[adjacentFaceId * 3 + 2]
                        );

                        if (
                            Math.abs(Math.acos(Math.round(Vector3.Dot(normal, v2) * 1000000) / 1000000)) <= limitAngle
                        ) {
                            // deal with FP Errors
                            selectedFaceIds[adjacentFaceId] = true;
                            recursiveExpandSelection(adjacentFaceId, iterCount - 1, normal);
                        }
                    } else {
                        selectedFaceIds[adjacentFaceId] = true;
                        recursiveExpandSelection(adjacentFaceId, iterCount - 1, normal);
                    }
                }
            }
        }
    }

    private updateNodeGeometry(mesh: Mesh) {
        if (mesh.geometry) {
            const indices = mesh.geometry.getIndices();
            const positions = mesh.geometry.getVerticesData(VertexBuffer.PositionKind);

            const v1 = new Vector3();
            const v2 = new Vector3();
            const v3 = new Vector3();
            const v4 = new Vector3();

            if (indices && positions) {
                const facesByEdges: { [key: string]: number[] } = {};
                /** Sets of three faces by index adjacent to each face by index */
                const faceNeighbors: number[][] = [];
                /** Sets of xyz scalars making up the face normals */
                const faceNormals: number[] = [];
                const facePositions: number[] = [];

                const trianglesCount = indices.length / 3;
                for (let triId = 0; triId < trianglesCount; triId++) {
                    const triOffset = triId * 3;
                    // Get the triangle position vertices
                    v1.copyFromFloats(
                        positions[indices[triOffset] * 3],
                        positions[indices[triOffset] * 3 + 1],
                        positions[indices[triOffset] * 3 + 2]
                    );
                    v2.copyFromFloats(
                        positions[indices[triOffset + 1] * 3],
                        positions[indices[triOffset + 1] * 3 + 1],
                        positions[indices[triOffset + 1] * 3 + 2]
                    );
                    v3.copyFromFloats(
                        positions[indices[triOffset + 2] * 3],
                        positions[indices[triOffset + 2] * 3 + 1],
                        positions[indices[triOffset + 2] * 3 + 2]
                    );

                    // Calculate the centroid of each face
                    v4.setAll(0);
                    v4.addInPlace(v1).addInPlace(v2).addInPlace(v3);
                    v4.scaleInPlace(1 / 3);
                    facePositions.push(v4.x, v4.y, v4.z);

                    // Get two of the triangle edges
                    v1.subtractInPlace(v2);
                    v2.subtractInPlace(v3);

                    // Get the triangle face normal with the two edges.
                    // Note that this is not the geometry normal, but the position-based face normal
                    Vector3.CrossToRef(v1, v2, v4);
                    const faceNormal = v4;
                    faceNormal.normalize();
                    faceNormals.push(faceNormal.x, faceNormal.y, faceNormal.z);

                    // Now we need to consolidate what triangles are next to each other by organizing them by edges
                    for (let i = 0; i < 3; i++) {
                        const idx1 = indices[triOffset + i];
                        const idx2 = indices[triOffset + ((i + 1) % 3)];

                        // This is not going to work for geometry seams like on cylinders.
                        // Possibly change from index ids to a representation of the vertex position
                        const edgeId = idx1 > idx2 ? `${idx1}-${idx2}` : `${idx2}-${idx1}`;
                        if (!facesByEdges[edgeId]) {
                            facesByEdges[edgeId] = [];
                        }

                        facesByEdges[edgeId].push(triId);
                    }
                }

                // Now we go through the edges and register the neighbors for each triangle
                for (const edgeId in facesByEdges) {
                    const faces = facesByEdges[edgeId];
                    if (!faceNeighbors[faces[0]]) {
                        faceNeighbors[faces[0]] = [];
                    }
                    // Some edges may only have one face if the face is on the edge of the geometry
                    if (faces.length > 1) {
                        if (!faceNeighbors[faces[1]]) {
                            faceNeighbors[faces[1]] = [];
                        }

                        faceNeighbors[faces[0]].push(faces[1]);
                        faceNeighbors[faces[1]].push(faces[0]);
                    }
                }

                const result = {
                    faceCount: trianglesCount,
                    faceNeighbors,
                    facePositions,
                    faceNormals,
                };
                meshFaceInfo.set(mesh.id, result);
                return result;
            }
        }
    }
}
